import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from '../i18n/LanguageContext';
import WordPuzzleGame from '../WordPuzzle';
import LetterFall from '../LetterFall';
import LetterEater from '../LetterEater';
import ConversationChallengePlayer from '../ConversationChallengePlayer';
import LearningClock from '../LearningClock';
import SceneExplorer from '../SceneExplorer';
import SoukBargainGame from '../modules/SoukBargainGame';
import SoukClothesGame from '../modules/SoukClothesGame';

const GamesSection = ({ isDarkMode, userToken, API, showNotification, hasPaidPlan, appSettings = {}, currentUser, onGameActive }) => {
  const { t } = useTranslation();
  // App-shell cache: games list rarely changes — render it from frame 1 so
  // /games/X refresh skips the games-list spinner before auto-resume kicks in.
  const _cachedGsGames = (() => {
    try { const c = localStorage.getItem('gsGamesCache'); if (c) return JSON.parse(c); } catch {}
    return null;
  })();
  const [games, setGames] = React.useState(_cachedGsGames || []);

  // URL → game ID lookup. Single source of truth: window.location.pathname.
  // If URL is /games/<Slug>, find the matching game. /games (no slug) returns
  // null → games dashboard renders. No localStorage middleman.
  const _gameSlugToIdHints = {
    'WordChallenge': 'word-challenge',
    'LearningClock': 'learning-clock',
    'ConversationChallenge': 'conversation-challenge',
    'SoukBargainGame': 'souk-bargain-game',
    'SoukLa7wayj': 'souk-clothes-game',
    'SceneExplorer': 'scene-explorer',
    'WordPuzzle': 'word-puzzle',
    'LetterFall': 'letter-fall',
    'LetterEater': 'letter-eater',
  };
  const _resolveGameFromUrl = (gamesList) => {
    if (!gamesList || gamesList.length === 0) return null;
    let slug = '';
    let scriptSeg = null;
    try {
      const path = window.location.pathname;
      if (!path.startsWith('/games/')) return null;
      const parts = path.split('/games/')[1].split('/');
      slug = parts[0];
      // Parts after the game slug are game-specific (e.g. WordChallenge:
      // /games/WordChallenge/<arabic|latin>/level<N>). Capture the script
      // segment if present and valid; deeper segments are read by the game
      // component itself.
      if (parts[1] === 'arabic' || parts[1] === 'latin') {
        scriptSeg = parts[1];
      }
      if (!slug || slug === 'MoroccoJourney') return null;
    } catch { return null; }
    const idHint = _gameSlugToIdHints[slug];
    if (!idHint) return null;
    const game = gamesList.find(g =>
      g.id === idHint
      || (idHint === 'learning-clock' && g.is_learning_clock)
      || (idHint === 'conversation-challenge' && g.is_conversation)
      || (idHint === 'souk-bargain-game' && g.is_souk_game)
      || (idHint === 'souk-clothes-game' && g.is_clothes_game)
      || (idHint === 'scene-explorer' && g.is_scene_explorer)
      || (idHint === 'word-puzzle' && g.is_word_puzzle)
      || (idHint === 'letter-fall' && g.is_letter_fall)
      || (idHint === 'letter-eater' && g.is_letter_eater)
    );
    if (!game) return null;
    let view = 'script-select';
    if (game.is_learning_clock) view = 'learning-clock';
    else if (game.is_conversation) view = 'conversation-list';
    else if (game.is_souk_game) view = 'souk-bargain-game';
    else if (game.is_clothes_game) view = 'souk-clothes-game';
    else if (game.is_scene_explorer) view = 'scene-explorer';
    else if (game.is_word_puzzle) view = 'word-puzzle';
    else if (game.is_letter_fall) view = 'letter-fall';
    else if (game.is_letter_eater) view = 'letter-eater';
    // For Word Challenge, presence of script in URL means user is past the
    // script-select screen → render WordChallengeGame directly.
    else if (scriptSeg) view = 'playing';
    return { game, view, script: scriptSeg };
  };

  // Synchronous initial resolve from URL + cached games (frame 1, no flash).
  const _initialAutoOpen = _resolveGameFromUrl(_cachedGsGames);

  const [selectedGame, setSelectedGame] = React.useState(_initialAutoOpen?.game || null);
  const [gameView, setGameView] = React.useState(_initialAutoOpen?.view || 'dashboard');
  const [loading, setLoading] = React.useState(!_cachedGsGames);
  const [showPremiumModal, setShowPremiumModal] = React.useState(false);
  const [selectedScript, setSelectedScript] = React.useState(_initialAutoOpen?.script || null); // 'arabic' or 'latin'
  
  // Conversation Challenge states
  const [conversations, setConversations] = React.useState([]);
  const [selectedConversation, setSelectedConversation] = React.useState(null);
  const [conversationSentences, setConversationSentences] = React.useState([]);
  const [currentSentenceIndex, setCurrentSentenceIndex] = React.useState(0);
  const [userAnswer, setUserAnswer] = React.useState('');
  const [feedback, setFeedback] = React.useState(null);
  const [conversationScore, setConversationScore] = React.useState(0);
  const [selectedGender, setSelectedGender] = React.useState(null); // 'male' or 'female'
  const [selectedConversationScript, setSelectedConversationScript] = React.useState(null); // 'arabic' or 'latin'
  const [showHint, setShowHint] = React.useState(false);
  const [attempts, setAttempts] = React.useState(0);
  const [streak, setStreak] = React.useState(0);
  const [earnedBadges, setEarnedBadges] = React.useState([]);
  const [isMusicPlaying, setIsMusicPlaying] = React.useState(false);
  const [musicAudio, setMusicAudio] = React.useState(null);
  const [showTyping, setShowTyping] = React.useState(false);
  const [showGameGuide, setShowGameGuide] = React.useState(false);
  const [showChoices, setShowChoices] = React.useState(false);

  React.useEffect(() => {
    loadGames();
  }, [appSettings]);

  // Restore the user's selected game from URL on Ctrl+R or first visit.
  // The sync useState init above handles the cached-games case (no flash).
  // This effect handles first-ever visit (when cached games weren't available
  // synchronously) — once games arrive from fetch, resolve from URL.
  React.useEffect(() => {
    if (games.length === 0) return;
    const resolved = _resolveGameFromUrl(games);
    if (!resolved) {
      // URL is /games (no game) — notify parent of any sync-restored game
      if (selectedGame && typeof onGameActive === 'function') onGameActive(selectedGame);
      return;
    }
    const { game } = resolved;
    // Already resolved synchronously — just notify parent
    if (selectedGame && selectedGame.id === game.id) {
      if (typeof onGameActive === 'function') onGameActive(game);
      return;
    }
    // First visit (no cached games at mount) — restore now from URL
    setSelectedGame(game);
    if (game.is_learning_clock) setGameView('learning-clock');
    else if (game.is_conversation) setGameView('conversation-list');
    else if (game.is_souk_game) setGameView('souk-bargain-game');
    else if (game.is_clothes_game) setGameView('souk-clothes-game');
    else if (game.is_scene_explorer) setGameView('scene-explorer');
    else if (game.is_word_puzzle) setGameView('word-puzzle');
    else if (game.is_letter_fall) setGameView('letter-fall');
    else if (game.is_letter_eater) setGameView('letter-eater');
    else setGameView('script-select'); // Word Challenge / default
    if (typeof onGameActive === 'function') onGameActive(game);
  }, [games]);

  // Cleanup music when component unmounts or when leaving conversation
  React.useEffect(() => {
    return () => {
      if (musicAudio) {
        musicAudio.pause();
        musicAudio.currentTime = 0;
        setIsMusicPlaying(false);
      }
    };
  }, [musicAudio]);

  // Notify parent when a game is active (hides Buddy Darija)
  React.useEffect(() => {
    if (onGameActive) onGameActive(gameView === 'dashboard' ? null : gameView);
    return () => { if (onGameActive) onGameActive(null); };
  }, [gameView]);

  // Stop music when navigating away from conversation playing screen
  React.useEffect(() => {
    if (gameView !== 'conversation-list' && gameView !== 'playing' && musicAudio) {
      musicAudio.pause();
      musicAudio.currentTime = 0;
      setIsMusicPlaying(false);
    }
  }, [gameView]);

  const loadGames = async () => {
    try {
      // Fetch assigned icons so we can apply them to all games (including hardcoded ones)
      let iconsByGame = {};
      try {
        const iconsRes = await fetch(`${API}/icons`);
        if (iconsRes.ok) {
          const iconsData = await iconsRes.json();
          for (const icon of (iconsData.icons || [])) {
            const assigned = Array.isArray(icon.assigned_to) ? icon.assigned_to : (icon.assigned_to ? [icon.assigned_to] : []);
            for (const gid of assigned) {
              iconsByGame[gid] = { icon_url: icon.url, icon_size: icon.icon_size || 'md' };
            }
          }
        }
      } catch (e) { console.error('Error loading icons:', e); }

      const response = await fetch(`${API}/games`);
      const data = await response.json();
      const gamesFromDB = data.games || [];
      
      // Get conversation challenge settings from its own API
      let convIsVisible = true;
      let convIsPro = false;
      let convData = null;
      try {
        const convRes = await fetch(`${API}/conversation-challenge/game-settings`);
        if (convRes.ok) {
          convData = await convRes.json();
          convIsVisible = convData.is_visible !== false;
          convIsPro = convData.is_pro || false;
        }
      } catch (e) { console.error('Error loading conversation challenge settings:', e); }
      
      // Add Conversation Challenge as a special game card (only if visible)
      const allGames = [...gamesFromDB];
      
      if (convIsVisible) {
        const conversationGame = {
          id: 'conversation-challenge',
          name: convData?.name || 'Conversation Challenge',
          description: convData?.description || 'Practice real conversations in Darija',
          icon: '💬',
          is_active: true,
          is_premium: convIsPro,
          is_conversation: true
        };
        allGames.push(conversationGame);
      }
      
      // Mark Learning Clock games for special handling
      allGames.forEach(game => {
        if (game.id === 'learning-clock') {
          game.is_learning_clock = true;
        }
      });
      
      // Add Souk Bargain Game (with visibility/access settings)
      try {
        const soukSettingsRes = await fetch(`${API}/souk/game-settings`);
        const soukSettings = await soukSettingsRes.json();
        
        // Only add game if it's active (visible)
        if (soukSettings.is_active !== false) {
          const soukGame = {
            id: 'souk-bargain-game',
            name: soukSettings.name || 'Souk Bargain Game',
            description: soukSettings.description || 'Learn Darija by shopping in a Moroccan souk!',
            icon: '🛍️',
            is_active: true,
            is_premium: soukSettings.is_pro_only || false,
            is_souk_game: true
          };
          allGames.push(soukGame);
        }
      } catch (error) {
        console.error('Error loading souk settings:', error);
        const soukGame = {
          id: 'souk-bargain-game',
          name: 'Souk Bargain Game',
          description: 'Learn Darija by shopping in a Moroccan souk!',
          icon: '🛍️',
          is_active: true,
          is_premium: false,
          is_souk_game: true
        };
        allGames.push(soukGame);
      }
      
      // Add Souk L7wayej (Clothes Game) with visibility/access settings
      try {
        const clothesSettingsRes = await fetch(`${API}/souk-clothes/settings`);
        if (clothesSettingsRes.ok) {
          const clothesSettings = await clothesSettingsRes.json();

          if (clothesSettings.is_active !== false) {
            const clothesGame = {
              id: 'souk-clothes-game',
              name: clothesSettings.name || 'Souk L7wayej',
              description: clothesSettings.description || 'Shop for traditional and modern Moroccan clothing, assemble outfits!',
              icon: '👗',
              is_active: true,
              is_premium: clothesSettings.is_pro_only || false,
              is_clothes_game: true
            };
            allGames.push(clothesGame);
          }
        }
      } catch (error) {
        console.error('Error loading clothes game settings:', error);
      }
      
      // Add Morocco Journey Game (with visibility/access settings from admin)
      try {
        const moroccoSettingsRes = await fetch(`${API}/game/settings`);

        if (moroccoSettingsRes.ok) {
          const moroccoSettings = await moroccoSettingsRes.json();

          // Only add game if it's visible
          if (moroccoSettings.is_visible !== false) {
            const moroccoJourneyGame = {
              id: 'morocco-journey-game',
              name: moroccoSettings.name || 'Morocco Journey',
              description: moroccoSettings.description || 'Explore Morocco, collect gems, and learn Darija through conversations!',
              icon: '🏜️',
              is_active: true,
              is_premium: moroccoSettings.is_pro === true, // PRO if is_pro is true
              is_morocco_journey: true
            };
            allGames.push(moroccoJourneyGame);
          }
        } else {
          console.error('Failed to fetch Morocco Journey settings:', moroccoSettingsRes.status);
          // Don't add game on error - keep it hidden until settings can be fetched
        }
      } catch (error) {
        console.error('Error loading Morocco Journey settings:', error);
        // Don't add game on error - keep it hidden until settings can be fetched
      }
      
      // Add Scene Explorer Game (with visibility/access settings)
      try {
        const sceneSettingsRes = await fetch(`${API}/scene-explorer/settings`);
        if (sceneSettingsRes.ok) {
          const sceneSettings = await sceneSettingsRes.json();
          if (sceneSettings.is_visible !== false) {
            const sceneExplorerGame = {
              id: 'scene-explorer',
              name: sceneSettings.name || 'Scene Explorer',
              description: sceneSettings.description || 'Explore visual scenes and learn vocabulary by identifying objects!',
              icon: '🖼️',
              is_active: true,
              is_premium: sceneSettings.is_premium || false,
              is_scene_explorer: true
            };
            allGames.push(sceneExplorerGame);
          }
        }
      } catch (error) {
        console.error('Error loading scene explorer settings:', error);
      }

      // Add Word Puzzle Game (fetch settings from backend)
      let wpVisible = true, wpPro = false, wpIcon = '🧩', wpName = 'Word Puzzle', wpDesc = 'Swipe Darija letters to solve crossword puzzles!', wpCardGradient = '', wpIconUrl = '';
      try {
        const wpRes = await fetch(`${API}/word-puzzle/game-settings`);
        if (wpRes.ok) {
          const wpData = await wpRes.json();
          wpVisible = wpData.is_visible !== false;
          wpPro = wpData.is_pro || false;
          wpIcon = wpData.icon || '🧩';
          wpName = wpData.name || 'Word Puzzle';
          wpDesc = wpData.description || 'Swipe Darija letters to solve crossword puzzles!';
          wpCardGradient = wpData.card_gradient || '';
          wpIconUrl = wpData.icon_url || '';
        }
      } catch (e) { console.error('Error loading word puzzle settings:', e); }

      if (wpVisible) {
        allGames.push({
          id: 'word-puzzle',
          name: wpName,
          description: wpDesc,
          icon: wpIcon,
          icon_url: wpIconUrl,
          is_active: true,
          is_premium: wpPro,
          is_word_puzzle: true,
          card_gradient: wpCardGradient
        });
      }

      // Add Letter Fall Game (fetch settings from backend)
      let lfVisible = true, lfPro = false, lfIcon = '🔤', lfName = 'Letter Fall', lfDesc = 'Catch the falling letters to spell Darija words.';
      try {
        const lfRes = await fetch(`${API}/letter-fall/game-settings`);
        if (lfRes.ok) {
          const lfData = await lfRes.json();
          lfVisible = lfData.is_visible !== false;
          lfPro = lfData.is_premium || false;
          lfIcon = lfData.icon || '🔤';
          lfName = lfData.name || 'Letter Fall';
          lfDesc = lfData.description || 'Catch the falling letters to spell Darija words.';
        }
      } catch (e) { console.error('Error loading letter fall settings:', e); }

      if (lfVisible) {
        allGames.push({
          id: 'letter-fall',
          name: lfName,
          description: lfDesc,
          icon: lfIcon,
          is_active: true,
          is_premium: lfPro,
          is_letter_fall: true,
        });
      }

      // Add Letter Eater Game (fetch settings from backend)
      let leVisible = true, lePro = false, leIcon = '👾', leName = 'Letter Eater', leDesc = 'Move the character through the maze and eat letters to spell Darija sentences.';
      try {
        const leRes = await fetch(`${API}/letter-eater/game-settings`);
        if (leRes.ok) {
          const leData = await leRes.json();
          leVisible = leData.is_visible !== false;
          lePro = leData.is_premium || false;
          leIcon = leData.icon || '👾';
          leName = leData.name || 'Letter Eater';
          leDesc = leData.description || 'Move the character through the maze and eat letters to spell Darija sentences.';
        }
      } catch (e) { console.error('Error loading letter eater settings:', e); }

      if (leVisible) {
        allGames.push({
          id: 'letter-eater',
          name: leName,
          description: leDesc,
          icon: leIcon,
          is_active: true,
          is_premium: lePro,
          is_letter_eater: true,
        });
      }

      // Apply icon assignments to ALL games (DB games + hardcoded special games)
      for (const game of allGames) {
        const iconData = iconsByGame[game.id];
        if (iconData) {
          game.icon_url = iconData.icon_url;
          game.icon_size = iconData.icon_size;
        }
      }

      setGames(allGames);
      try { localStorage.setItem('gsGamesCache', JSON.stringify(allGames)); } catch {}
      setLoading(false);
    } catch (error) {
      console.error('Error loading games:', error);
      setLoading(false);
    }
  };

  const handleGameClick = (game) => {
    // Check if game is premium and user doesn't have paid plan
    if (game.is_premium && !hasPaidPlan) {
      setShowPremiumModal(true);
      setSelectedGame(game);
      return;
    }
    
    // Check if this is the Learning Clock
    if (game.is_learning_clock) {
      // Update URL for SEO
      window.history.pushState({}, '', '/games/LearningClock');
      setGameView('learning-clock');
      setSelectedGame(game);
      return;
    }
    
    // Check if this is the Conversation Challenge
    if (game.is_conversation) {
      // Update URL for SEO
      window.history.pushState({}, '', '/games/ConversationChallenge');
      setGameView('conversation-list');
      setSelectedGame(game);
      return;
    }
    
    // Check if this is the Souk Bargain Game
    if (game.is_souk_game) {
      // Update URL for SEO
      window.history.pushState({}, '', '/games/SoukBargainGame');
      setGameView('souk-bargain-game');
      setSelectedGame(game);
      return;
    }
    
    // Check if this is the Souk Clothes Game
    if (game.is_clothes_game) {
      // Update URL for SEO
      window.history.pushState({}, '', '/games/SoukLa7wayj');
      setGameView('souk-clothes-game');
      setSelectedGame(game);
      return;
    }
    
    // Check if this is the Scene Explorer Game
    if (game.is_scene_explorer) {
      window.history.pushState({}, '', '/games/SceneExplorer');
      setGameView('scene-explorer');
      setSelectedGame(game);
      return;
    }

    // Check if this is the Word Puzzle Game
    if (game.is_word_puzzle) {
      // Update URL for SEO + refresh-restore
      window.history.pushState({}, '', '/games/WordPuzzle');
      setGameView('word-puzzle');
      setSelectedGame(game);
      return;
    }

    // Check if this is the Letter Fall Game
    if (game.is_letter_fall) {
      window.history.pushState({}, '', '/games/LetterFall');
      setGameView('letter-fall');
      setSelectedGame(game);
      return;
    }

    // Check if this is the Letter Eater Game
    if (game.is_letter_eater) {
      window.history.pushState({}, '', '/games/LetterEater');
      setGameView('letter-eater');
      setSelectedGame(game);
      return;
    }

    // Check if this is the Morocco Journey Game
    if (game.is_morocco_journey) {
      // Open Morocco Journey in same tab with new URL
      window.location.href = '/games/MoroccoJourney';
      return;
    }
    
    // Default: Word Challenge or other games
    // Update URL for SEO
    window.history.pushState({}, '', '/games/WordChallenge');
    setSelectedGame(game);
    setGameView('script-select');
  };

  const handleScriptSelect = (script) => {
    setSelectedScript(script);
    setGameView('playing');
    // Push URL so refresh keeps script choice. Only Word Challenge uses path.
    if (selectedGame && (selectedGame.id === 'word-challenge' || selectedGame.is_word_challenge)) {
      try { window.history.pushState({}, '', `/games/WordChallenge/${script}`); } catch {}
    }
  };

  // Conversation Challenge functions
  const loadConversations = async () => {
    try {
      const response = await fetch(`${API}/conversations`, {
        headers: userToken ? { 'Authorization': `Bearer ${userToken}` } : {}
      });
      const data = await response.json();
      setConversations(data.conversations || []);
    } catch (error) {
      console.error('Error loading conversations:', error);
    }
  };

  const startConversation = async (conversation) => {
    console.log('Starting conversation:', conversation);
    console.log('Conversation is_free:', conversation.is_free);
    console.log('User hasPaidPlan:', hasPaidPlan);
    
    // Check if conversation is locked (PRO only) and user doesn't have paid plan
    // is_free === false means it's PRO only
    if (conversation.is_free === false && !hasPaidPlan) {
      console.log('🔒 Conversation is PRO and user is not paid - redirecting to membership');
      showNotification('🔒 This conversation requires PRO membership. Redirecting...', 'error');
      
      // Redirect directly to membership page (immediate)
      window.location.href = window.location.origin + '/membership';
      return; // Stop execution
    }
    
    try {
      setLoading(true);
      setSelectedConversation(conversation);

      // Load sentences for this conversation
      const response = await fetch(`${API}/conversations/${conversation.id}/sentences`, {
        headers: userToken ? { 'Authorization': `Bearer ${userToken}` } : {}
      });
      
      console.log('Sentences API response status:', response.status);
      
      if (response.ok) {
        const data = await response.json();
        console.log('Loaded sentences:', data.sentences?.length || 0);
        setConversationSentences(data.sentences || []);
        setCurrentSentenceIndex(0);
        setUserAnswer('');
        setFeedback(null);
        setConversationScore(0);
        setGameView('conversation-playing');
      } else {
        const errorData = await response.json().catch(() => ({}));
        console.error('Failed to load conversation:', errorData);
        showNotification('Failed to load conversation: ' + (errorData.detail || 'Unknown error'), 'error');
      }
    } catch (error) {
      console.error('Error loading conversation:', error);
      showNotification('Failed to load conversation: ' + error.message, 'error');
    } finally {
      setLoading(false);
    }
  };

  const checkConversationAnswer = async () => {
    if (!userAnswer.trim()) {
      showNotification('Please enter an answer', 'info');
      return;
    }

    const currentSentence = conversationSentences[currentSentenceIndex];
    setAttempts(prev => prev + 1);
    
    try {
      const formData = new FormData();
      formData.append('sentence_id', currentSentence.id);
      formData.append('user_answer', userAnswer);
      if (userToken) formData.append('user_token', userToken);

      const response = await fetch(`${API}/conversations/${selectedConversation.id}/check-answer`, {
        method: 'POST',
        body: formData
      });
      
      const data = await response.json();
      
      // Calculate partial credit
      const normalizedAnswer = userAnswer.toLowerCase().trim();
      const correctAnswers = currentSentence.darija_answers || [];
      let matchedWords = 0;
      let totalWords = 0;
      
      correctAnswers.forEach(answer => {
        const words = answer.toLowerCase().split(/\s+/);
        totalWords = Math.max(totalWords, words.length);
        words.forEach(word => {
          if (normalizedAnswer.includes(word)) {
            matchedWords++;
          }
        });
      });
      
      // Give partial credit if at least 1 word matches
      let earnedPoints = 0;
      if (data.correct) {
        earnedPoints = currentSentence.score_value || 10;
      } else if (matchedWords > 0) {
        earnedPoints = 0.5; // Partial credit
        data.partial = true;
        data.matched_words = matchedWords;
      }
      
      setFeedback({...data, score_earned: earnedPoints});
      
      if (earnedPoints > 0) {
        setConversationScore(prev => prev + earnedPoints);
      }
      
      // Update streak
      if (data.correct) {
        setStreak(prev => prev + 1);
        
        // Check for streak badges
        const newStreak = streak + 1;
        if (newStreak === 3 && !earnedBadges.find(b => b.name === 'Hot Streak')) {
          setEarnedBadges(prev => [...prev, { name: 'Hot Streak', icon: '🔥' }]);
          showNotification('🔥 Hot Streak Badge Earned!', 'success');
        }
        if (newStreak === 5 && !earnedBadges.find(b => b.name === 'On Fire')) {
          setEarnedBadges(prev => [...prev, { name: 'On Fire', icon: '🌟' }]);
          showNotification('🌟 On Fire Badge Earned!', 'success');
        }
      } else {
        setStreak(0); // Reset streak on wrong answer
      }
      
      // Check for first chat badge
      if (currentSentenceIndex === 0 && data.correct && !earnedBadges.find(b => b.name === 'First Chat')) {
        setEarnedBadges(prev => [...prev, { name: 'First Chat', icon: '🎖️' }]);
        showNotification('🎖️ First Chat Badge Earned!', 'success');
      }
      
      // Check for perfect conversation badge
      if (currentSentenceIndex === conversationSentences.length - 1 && data.correct) {
        const allCorrect = conversationScore + earnedPoints === conversationSentences.length * 10;
        if (allCorrect && !earnedBadges.find(b => b.name === 'Perfect Score')) {
          setEarnedBadges(prev => [...prev, { name: 'Perfect Score', icon: '💯' }]);
          showNotification('💯 Perfect Score Badge Earned!', 'success');
        }
      }
    } catch (error) {
      console.error('Error checking answer:', error);
      showNotification('Failed to check answer', 'error');
    }
  };

  const nextConversationSentence = () => {
    if (currentSentenceIndex < conversationSentences.length - 1) {
      // Show typing indicator
      setShowTyping(true);
      setTimeout(() => {
        setShowTyping(false);
        setCurrentSentenceIndex(prev => prev + 1);
        setUserAnswer('');
        setFeedback(null);
        setShowHint(false);
        setAttempts(0);
        setShowChoices(false); // Hide choices for next sentence
      }, 1000); // 1 second delay to show "typing..."
    } else {
      // Conversation complete
      const accuracy = Math.round((conversationScore / (conversationSentences.length * 10)) * 100);
      showNotification(`🎉 Conversation complete! Score: ${conversationScore} | Accuracy: ${accuracy}%`, 'success');
      setGameView('conversation-list');
      setSelectedGender(null);
      setSelectedConversationScript(null);
    }
  };

  // NEW: Conversation Challenge Player (replaces old multi-screen flow)
  if (gameView === 'conversation-list' || gameView === 'conversation-playing') {
    return (
      <ConversationChallengePlayer
        isDarkMode={isDarkMode}
        userToken={userToken}
        userId={currentUser?.email || currentUser?.id}
        hasPaidPlan={hasPaidPlan}
        showNotification={showNotification}
        currentUser={currentUser}
        onBack={() => {
          // Reset URL to games page
          window.history.pushState({}, '', '/games');
          setGameView('dashboard');
          setSelectedGame(null);
          setSelectedGender(null);
          setSelectedConversationScript(null);
        }}
      />
    );
  }

  // Learning Clock View
  if (gameView === 'learning-clock') {
    return (
      <LearningClock
        isDarkMode={isDarkMode}
        userToken={userToken}
        API={API}
        showNotification={showNotification}
        onBack={() => {
          // Reset URL to games page
          window.history.pushState({}, '', '/games');
          setGameView('dashboard');
          setSelectedGame(null);
        }}
      />
    );
  }


  // Souk Bargain Game View
  if (gameView === 'souk-bargain-game') {
    return (
      <div>
        <button
          onClick={() => {
            // Reset URL to games page
            window.history.pushState({}, '', '/games');
            setGameView('dashboard');
            setSelectedGame(null);
          }}
          className={`mb-4 px-4 py-2 rounded-lg font-medium ${
            isDarkMode ? 'bg-gray-800 hover:bg-gray-700' : 'bg-gray-200 hover:bg-gray-300'
          }`}
        >
          ← Back to Games
        </button>
        <SoukBargainGame
          API={API}
          isDarkMode={isDarkMode}
          userToken={userToken}
          userId={currentUser?.id}
        />
      </div>
    );
  }
  
  // Souk Clothes Game View
  if (gameView === 'souk-clothes-game') {
    return (
      <SoukClothesGame
        API={API}
        currentUser={currentUser}
        hasPaidPlan={hasPaidPlan}
        onBack={() => {
          // Reset URL to games page
          window.history.pushState({}, '', '/games');
          setGameView('dashboard');
          setSelectedGame(null);
        }}
      />
    );
  }

  // Scene Explorer Game View
  if (gameView === 'scene-explorer') {
    return (
      <SceneExplorer
        API={API}
        userToken={userToken}
        isDarkMode={isDarkMode}
        showNotification={showNotification}
        hasPaidPlan={hasPaidPlan}
        onBack={() => {
          window.history.pushState({}, '', '/games');
          setGameView('dashboard');
          setSelectedGame(null);
        }}
      />
    );
  }

  // Letter Eater Game View
  if (gameView === 'letter-eater') {
    return (
      <LetterEater
        API={API}
        userToken={userToken}
        isDarkMode={isDarkMode}
        showNotification={showNotification}
        hasPaidPlan={hasPaidPlan}
        onBack={() => {
          window.history.pushState({}, '', '/games');
          setGameView('dashboard');
          setSelectedGame(null);
        }}
      />
    );
  }

  // Word Puzzle Game View
  if (gameView === 'word-puzzle') {
    return (
      <WordPuzzleGame
        userToken={userToken}
        isDarkMode={isDarkMode}
        showNotification={showNotification}
        onBack={() => {
          window.history.pushState({}, '', '/games');
          setGameView('dashboard');
          setSelectedGame(null);
        }}
      />
    );
  }

  // Letter Fall Game View
  if (gameView === 'letter-fall') {
    return (
      <LetterFall
        API={API}
        userToken={userToken}
        isDarkMode={isDarkMode}
        showNotification={showNotification}
        hasPaidPlan={hasPaidPlan}
        onBack={() => {
          window.history.pushState({}, '', '/games');
          setGameView('dashboard');
          setSelectedGame(null);
        }}
      />
    );
  }

  // Script Selection Screen for Word Challenge
  if (gameView === 'script-select' && selectedGame) {
    return (
      <div className="max-w-2xl mx-auto py-8 px-4">
        <button
          onClick={() => {
            setGameView('dashboard');
            setSelectedGame(null);
            try { window.history.pushState({}, '', '/games'); } catch {}
          }}
          className={`mb-6 flex items-center gap-2 px-4 py-2 rounded-lg transition-all ${
            isDarkMode
              ? 'bg-gray-700 hover:bg-gray-600 text-white'
              : 'bg-gray-200 hover:bg-gray-300 text-gray-800'
          }`}
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
          Back to Games
        </button>
        
        <div className="text-center mb-8">
          <div className="text-5xl mb-4">{selectedGame.icon || '🎮'}</div>
          <h2 className={`text-2xl font-bold mb-2 ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>
            {selectedGame.name}
          </h2>
          <p className={`${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
            Choose your preferred script to play
          </p>
        </div>
        
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {/* Arabic Script Option */}
          <button
            onClick={() => handleScriptSelect('arabic')}
            className={`p-6 rounded-2xl border-2 transition-all transform hover:scale-105 ${
              isDarkMode 
                ? 'bg-gradient-to-br from-gray-800 to-gray-900 border-purple-500 hover:border-purple-400' 
                : 'bg-gradient-to-br from-purple-50 to-blue-50 border-purple-300 hover:border-purple-500'
            }`}
          >
            <div className="text-4xl mb-3">🇲🇦</div>
            <h3 className={`text-xl font-bold mb-2 ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>
              Arabic Script
            </h3>
            <p className={`text-2xl font-arabic mb-2 ${isDarkMode ? 'text-purple-400' : 'text-purple-600'}`}>
              الدارجة
            </p>
            <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
              Practice reading Darija in Arabic letters
            </p>
          </button>
          
          {/* Latin Script Option */}
          <button
            onClick={() => handleScriptSelect('latin')}
            className={`p-6 rounded-2xl border-2 transition-all transform hover:scale-105 ${
              isDarkMode 
                ? 'bg-gradient-to-br from-gray-800 to-gray-900 border-blue-500 hover:border-blue-400' 
                : 'bg-gradient-to-br from-blue-50 to-green-50 border-blue-300 hover:border-blue-500'
            }`}
          >
            <div className="text-4xl mb-3">🔤</div>
            <h3 className={`text-xl font-bold mb-2 ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>
              {t('games_latin_script')}
            </h3>
            <p className={`text-2xl font-bold mb-2 ${isDarkMode ? 'text-blue-400' : 'text-blue-600'}`}>
              {t('darija_label')}
            </p>
            <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
              {t('games_practice_latin')}
            </p>
          </button>
        </div>
      </div>
    );
  }

  if (gameView === 'playing' && selectedGame && selectedScript) {
    return (
      <WordChallengeGame
        game={selectedGame}
        selectedScript={selectedScript}
        isDarkMode={isDarkMode}
        userToken={userToken}
        API={API}
        showNotification={showNotification}
        onBack={() => {
          setGameView('script-select');
          setSelectedScript(null);
          try { window.history.pushState({}, '', '/games/WordChallenge'); } catch {}
        }}
      />
    );
  }

  // Anti-flash: if URL points to a specific game (/games/<slug>) but the
  // resolver hasn't matched it yet (stale cache or first-ever visit), show a
  // blank placeholder instead of the dashboard until loadGames completes.
  // Without this, refreshing a newly-added game flashes the dashboard for one
  // paint before the useEffect on [games] restores the correct view.
  if (gameView === 'dashboard' && !selectedGame) {
    try {
      const p = window.location.pathname;
      if (p.startsWith('/games/') && p.split('/games/')[1].split('/')[0]) {
        return <div className="space-y-4 pt-4" />;
      }
    } catch {}
  }

  return (
    <div className="space-y-4 pt-4">
      <div className="text-center py-4">
        <h2 className={`text-3xl font-bold mb-2 ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>
          🎮 {t('games_title')}
        </h2>
        <p className={`text-lg ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
          {t('games_subtitle')}
        </p>
      </div>

      {loading ? (
        <div className="text-center py-12">
          <div className="animate-spin inline-block w-12 h-12 border-4 border-blue-500 border-t-transparent rounded-full"></div>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {[...games].sort((a, b) => Number(!!a.is_premium) - Number(!!b.is_premium)).map((game) => (
            <div
              key={game.id}
              className={`relative group rounded-2xl p-6 transition-all duration-300 transform hover:-translate-y-2 cursor-pointer ${
                game.card_gradient ? 'shadow-xl hover:shadow-2xl' :
                isDarkMode
                  ? 'bg-gradient-to-br from-gray-800 to-gray-900 hover:from-gray-700 hover:to-gray-800 shadow-xl hover:shadow-2xl'
                  : 'bg-gradient-to-br from-white to-gray-50 hover:from-blue-50 hover:to-purple-50 shadow-lg hover:shadow-2xl'
              } border ${isDarkMode ? 'border-gray-700' : 'border-gray-200'}`}
              style={game.card_gradient ? { background: game.card_gradient } : {}}
              onClick={() => handleGameClick(game)}
            >
              {game.is_premium && (
                <div className="absolute top-4 right-4">
                  {hasPaidPlan ? (
                    <div className="bg-gradient-to-r from-yellow-400 to-orange-500 text-white px-3 py-1 rounded-full text-xs font-bold shadow-lg">
                      ⭐ PRO
                    </div>
                  ) : (
                    <div className="bg-gray-600 text-white px-3 py-1 rounded-full text-xs font-bold shadow-lg">
                      🔒 PRO
                    </div>
                  )}
                </div>
              )}

              <div className="text-center">
                <div className="mb-4 transform group-hover:scale-110 transition-transform duration-300 flex justify-center">
                  {game.icon_url ? (
                    <img
                      src={`${API.replace('/api', '')}${game.icon_url}`}
                      alt={game.name}
                      className={`${{sm:'w-12 h-12',md:'w-16 h-16',lg:'w-24 h-24',xl:'w-32 h-32'}[game.icon_size] || (!isNaN(game.icon_size) ? '' : 'w-16 h-16')} object-contain`}
                      style={game.icon_size && !isNaN(game.icon_size) ? { width: Number(game.icon_size), height: Number(game.icon_size) } : undefined}
                    />
                  ) : game.is_letter_fall ? (
                    /* Fancy animated icon for Letter Fall — 3 colorful letter tiles bobbing */
                    <span className="lf-icon-tile">
                      <span className="lf-mini lf-mini-a">A</span>
                      <span className="lf-mini lf-mini-b">B</span>
                      <span className="lf-mini lf-mini-c">C</span>
                      <style>{`
                        .lf-icon-tile {
                          width: 112px; height: 112px;
                          border-radius: 24px;
                          background: linear-gradient(160deg, #ffffff 0%, #f5f5f8 100%);
                          box-shadow: 0 6px 18px rgba(10,15,28,0.10), inset 0 -3px 0 rgba(10,15,28,0.04);
                          border: 1px solid rgba(10,15,28,0.08);
                          position: relative;
                          display: inline-block;
                          overflow: hidden;
                        }
                        .lf-mini {
                          position: absolute;
                          width: 32px; height: 32px;
                          border-radius: 9px;
                          display: grid; place-items: center;
                          font-family: 'Space Grotesk', 'Inter', sans-serif;
                          font-weight: 800;
                          font-size: 19px;
                          color: white;
                          box-shadow: 0 4px 10px rgba(10,15,28,0.18), inset 0 -2px 0 rgba(0,0,0,0.10);
                        }
                        .lf-mini-a { background: linear-gradient(135deg, #F5B68F, #E96B9D); left: 14px; top: 22px; transform: rotate(-6deg); }
                        .lf-mini-b { background: linear-gradient(135deg, #3B6CEC, #B299E0); left: 40px; top: 12px; }
                        .lf-mini-c { background: linear-gradient(135deg, #93D3B5, #3B6CEC); left: 66px; top: 30px; transform: rotate(8deg); }
                        /* Sparkles — static dots for charm */
                        .lf-icon-tile::before, .lf-icon-tile::after {
                          content: ''; position: absolute; width: 6px; height: 6px;
                          border-radius: 50%; background: #FFD180;
                          box-shadow: 0 0 8px rgba(255,209,128,0.7);
                        }
                        .lf-icon-tile::before { top: 12px; right: 14px; }
                        .lf-icon-tile::after { bottom: 12px; left: 8px; background: #B299E0; box-shadow: 0 0 8px rgba(178,153,224,0.7); }
                      `}</style>
                    </span>
                  ) : game.is_letter_eater ? (
                    /* Fancy maze-style icon for Letter Eater — blob character chasing letter dots */
                    <span className="le-icon-tile">
                      <span className="le-dot le-dot-a">a</span>
                      <span className="le-dot le-dot-b">b</span>
                      <span className="le-dot le-dot-c">c</span>
                      <span className="le-blob"><span className="le-eye le-eye-l"></span><span className="le-eye le-eye-r"></span></span>
                      <style>{`
                        .le-icon-tile {
                          width: 112px; height: 112px;
                          border-radius: 24px;
                          background: linear-gradient(160deg, #0A0F1C 0%, #1a2235 100%);
                          box-shadow: 0 6px 18px rgba(10,15,28,0.30), inset 0 -3px 0 rgba(0,0,0,0.20);
                          border: 1px solid rgba(255,255,255,0.06);
                          position: relative;
                          display: inline-block;
                          overflow: hidden;
                        }
                        .le-icon-tile::before, .le-icon-tile::after {
                          content: '';
                          position: absolute;
                          background: linear-gradient(135deg, #5b87ff, #2554c7);
                          border-radius: 5px;
                          box-shadow: inset 0 1px 0 rgba(255,255,255,0.22);
                        }
                        .le-icon-tile::before { top: 14px; left: 14px; width: 18px; height: 18px; }
                        .le-icon-tile::after  { bottom: 14px; right: 14px; width: 18px; height: 18px; }
                        .le-dot {
                          position: absolute;
                          width: 18px; height: 18px;
                          border-radius: 50%;
                          background: rgba(255,224,102,0.18);
                          color: #ffe066;
                          font-family: 'Space Grotesk', sans-serif;
                          font-weight: 800; font-size: 12px;
                          display: grid; place-items: center;
                          box-shadow: 0 0 8px rgba(255,224,102,0.5),
                                      inset 0 0 0 1.5px rgba(255,224,102,0.55);
                        }
                        .le-dot-a { top: 14px; right: 14px; }
                        .le-dot-b { bottom: 14px; left: 14px; }
                        .le-dot-c { top: 50%; left: 50%; transform: translate(-50%, -50%); }
                        .le-blob {
                          position: absolute;
                          width: 28px; height: 28px;
                          top: 52%; left: 22%;
                          transform: translateY(-50%);
                          border-radius: 50% 50% 45% 45% / 55% 55% 45% 45%;
                          background: radial-gradient(circle at 30% 30%, #ff9bc5, #ff6b9d 50%, #b239e0 100%);
                          box-shadow: 0 0 10px rgba(255,107,157,0.5);
                        }
                        .le-eye {
                          position: absolute;
                          width: 6px; height: 6px;
                          top: 30%;
                          background: white;
                          border-radius: 50%;
                        }
                        .le-eye::after {
                          content: '';
                          position: absolute;
                          width: 60%; height: 60%;
                          top: 22%; right: 0;
                          background: #0A0F1C;
                          border-radius: 50%;
                        }
                        .le-eye-l { left: 18%; }
                        .le-eye-r { right: 18%; }
                      `}</style>
                    </span>
                  ) : (
                    <span className="flex items-center justify-center w-28 h-28 rounded-3xl bg-white shadow-lg text-7xl border border-gray-200">
                      {game.icon}
                    </span>
                  )}
                </div>
                <h3 className={`text-xl font-bold mb-2 ${game.card_gradient || isDarkMode ? 'text-white' : 'text-gray-800'}`}>
                  {game.name}
                </h3>
                <p className={`text-sm mb-4 ${game.card_gradient ? 'text-gray-200' : isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                  {game.description}
                </p>
                <button
                  className={`w-full py-3 rounded-lg font-bold transition-all shadow-md hover:shadow-lg ${
                    game.is_premium && !hasPaidPlan
                      ? 'bg-gray-500 hover:bg-gray-600'
                      : 'bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700'
                  } text-white`}
                >
                  {game.is_premium && !hasPaidPlan ? '🔒 Upgrade to Play' : '▶ ' + t('play').toUpperCase()}
                </button>
              </div>
            </div>
          ))}

          {/* Premium Modal */}
          {showPremiumModal && (
            <div className="fixed inset-0 bg-black bg-opacity-70 flex items-center justify-center z-50 p-4">
              <div className={`rounded-xl p-8 max-w-md w-full text-center ${isDarkMode ? 'bg-gray-800' : 'bg-white'} shadow-2xl`}>
                <div className="text-6xl mb-4">🔒</div>
                <h3 className={`text-2xl font-bold mb-4 ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>
                  Premium Feature
                </h3>
                <p className={`text-lg mb-6 ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                  This game is exclusive to Pro members. Upgrade your membership to unlock all premium games and features!
                </p>
                <div className="flex gap-3">
                  <button
                    onClick={() => setShowPremiumModal(false)}
                    className={`flex-1 py-3 rounded-lg font-semibold ${
                      isDarkMode ? 'bg-gray-700 hover:bg-gray-600 text-white' : 'bg-gray-200 hover:bg-gray-300 text-gray-700'
                    }`}
                  >
                    Maybe Later
                  </button>
                  <button
                    onClick={() => {
                      setShowPremiumModal(false);
                      window.location.href = '/membership';
                    }}
                    className="flex-1 py-3 bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white rounded-lg font-bold shadow-lg"
                  >
                    Upgrade to Pro →
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

const ScriptSelectionScreen = ({ game, isDarkMode, onScriptSelect, onBack, isPremiumLocked }) => {
  const availableScripts = [];
  
  if (game.allow_arabic_script) {
    availableScripts.push({
      type: 'arabic',
      name: 'Arabic Script',
      icon: '🔤',
      description: 'Learn Darija using Arabic characters',
      example: 'بغيت ناكول'
    });
  }
  
  if (game.allow_latin_script) {
    availableScripts.push({
      type: 'latin',
      name: 'Latin Script',
      icon: 'ABC',
      description: 'Learn Darija using Latin characters',
      example: 'bghit nakol'
    });
  }

  // If only one script is available and not premium locked, auto-select it
  React.useEffect(() => {
    if (availableScripts.length === 1 && !isPremiumLocked) {
      onScriptSelect(availableScripts[0].type);
    }
  }, [isPremiumLocked]);

  return (
    <div className={`space-y-6 max-w-4xl mx-auto relative ${isPremiumLocked ? 'blur-sm pointer-events-none' : ''}`}>
      {/* Back Button */}
      <button
        onClick={onBack}
        className={`px-4 py-2 rounded-lg font-semibold transition-all ${
          isDarkMode ? 'bg-gray-700 hover:bg-gray-600 text-gray-300' : 'bg-gray-200 hover:bg-gray-300 text-gray-700'
        }`}
      >
        ← Back
      </button>

      {/* Header */}
      <div className="text-center py-8">
        <div className="text-6xl mb-4">{game.icon}</div>
        <h2 className={`text-3xl font-bold mb-3 ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>
          Select Script
        </h2>
        <p className={`text-lg ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
          Choose how you want to learn Darija
        </p>
      </div>

      {/* Script Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {availableScripts.map((script) => (
          <div
            key={script.type}
            onClick={() => onScriptSelect(script.type)}
            className={`group rounded-2xl p-8 transition-all duration-300 transform hover:-translate-y-2 cursor-pointer ${
              isDarkMode
                ? 'bg-gradient-to-br from-gray-800 to-gray-900 hover:from-blue-900 hover:to-purple-900 shadow-xl hover:shadow-2xl'
                : 'bg-gradient-to-br from-white to-gray-50 hover:from-blue-50 hover:to-purple-50 shadow-lg hover:shadow-2xl'
            } border-2 ${isDarkMode ? 'border-gray-700 hover:border-blue-500' : 'border-gray-200 hover:border-blue-400'}`}
          >
            <div className="text-center">
              <div className="text-7xl mb-4 transform group-hover:scale-110 transition-transform duration-300">
                {script.icon}
              </div>
              <h3 className={`text-2xl font-bold mb-3 ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>
                {script.name}
              </h3>
              <p className={`text-sm mb-4 ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                {script.description}
              </p>
              <div className={`p-4 rounded-lg mb-6 ${isDarkMode ? 'bg-gray-700' : 'bg-gray-100'}`}>
                <p className={`text-xs mb-1 ${isDarkMode ? 'text-gray-500' : 'text-gray-500'}`}>
                  Example:
                </p>
                <p className={`text-xl font-bold ${isDarkMode ? 'text-blue-400' : 'text-blue-600'}`}>
                  {script.example}
                </p>
              </div>
              <button className="w-full py-4 bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white rounded-lg font-bold text-lg shadow-md hover:shadow-xl transition-all">
                Select {script.name} →
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};


const WordChallengeGame = ({ game, selectedScript, isDarkMode, userToken, API, showNotification, onBack }) => {
  const { t } = useTranslation();
  // Read URL on mount: /games/WordChallenge/<script>/level<N> means refresh
  // inside a level — auto-resume so the user stays on the same screen.
  const _wantedLevelFromUrl = (() => {
    try {
      const path = window.location.pathname;
      const parts = path.split('/');
      // ['', 'games', 'WordChallenge', '<script>', 'level<N>']
      if (parts[1] === 'games' && parts[2] === 'WordChallenge' && parts[4] && parts[4].startsWith('level')) {
        const n = parseInt(parts[4].slice(5), 10);
        if (!isNaN(n)) return n;
      }
    } catch {}
    return null;
  })();
  const [gameState, setGameState] = React.useState(_wantedLevelFromUrl != null ? 'playing' : 'level-select'); // level-select, playing, results
  const [selectedLevel, setSelectedLevel] = React.useState(null);
  const [levels, setLevels] = React.useState([]);
  const [questions, setQuestions] = React.useState([]);
  const [currentQuestionIndex, setCurrentQuestionIndex] = React.useState(0);
  const [userAnswer, setUserAnswer] = React.useState('');
  const [score, setScore] = React.useState(0);
  const [attempts, setAttempts] = React.useState(0);
  const [maxAttempts, setMaxAttempts] = React.useState(5);
  const [timeLeft, setTimeLeft] = React.useState(60);
  const [hintsUsed, setHintsUsed] = React.useState(0);
  const [hintsAvailable, setHintsAvailable] = React.useState(0);
  const [showHint, setShowHint] = React.useState(false);
  const [hintText, setHintText] = React.useState('');
  const [feedback, setFeedback] = React.useState(null);
  const [correctAnswers, setCorrectAnswers] = React.useState(0);
  const [loading, setLoading] = React.useState(true);
  const [showCelebration, setShowCelebration] = React.useState(false);
  const [showTimeWarning, setShowTimeWarning] = React.useState(false);
  const [showTimeUpModal, setShowTimeUpModal] = React.useState(false);
  const [heartLossIndex, setHeartLossIndex] = React.useState(-1); // Track which heart was just lost
  
  // Audio states
  const [isMuted, setIsMuted] = React.useState(false);
  const audioRef = React.useRef(null);
  
  // Exit confirmation modal
  const [showExitModal, setShowExitModal] = React.useState(false);

  React.useEffect(() => {
    if (gameState === 'level-select') {
      loadLevels();
    }
  }, [gameState]);

  // Auto-resume: if URL has ?level=<id> on mount, fetch that level immediately
  // so refresh inside an active level keeps the user on the playing screen.
  // Intentionally runs once on mount only.
  React.useEffect(() => {
    if (_wantedLevelFromUrl != null && gameState === 'playing' && !selectedLevel) {
      startLevel({ level: _wantedLevelFromUrl });
    }
  }, []); // eslint-disable-line

  // Mirror gameState + selectedLevel to URL so refresh keeps the user where they were.
  // Path-based: /games/WordChallenge/<script>[/level<N>]
  React.useEffect(() => {
    try {
      const scriptSeg = selectedScript || 'arabic';
      if (gameState === 'playing' && selectedLevel?.level != null) {
        window.history.replaceState({}, '', `/games/WordChallenge/${scriptSeg}/level${selectedLevel.level}`);
      } else if (gameState === 'level-select') {
        window.history.replaceState({}, '', `/games/WordChallenge/${scriptSeg}`);
      }
    } catch {}
  }, [gameState, selectedLevel, selectedScript]);

  // Audio player effect
  React.useEffect(() => {
    if (game.audio_url && audioRef.current) {
      if (gameState === 'playing') {
        // Play music when game starts
        audioRef.current.play().catch(err => console.log('Audio play failed:', err));
        setIsMuted(false);
      } else {
        // Stop music when not playing
        audioRef.current.pause();
        audioRef.current.currentTime = 0;
        setIsMuted(true);
      }
    }
  }, [gameState, game.audio_url]);

  React.useEffect(() => {
    if (gameState === 'playing' && timeLeft > 0) {
      const timer = setTimeout(() => setTimeLeft(timeLeft - 1), 1000);
      
      // Show warning when 10 seconds left or 25% of time remaining
      const warningTime = Math.max(10, Math.floor(selectedLevel?.time_per_question * 0.25));
      if (timeLeft === warningTime) {
        setShowTimeWarning(true);
        setTimeout(() => setShowTimeWarning(false), 3000);
      }
      
      return () => clearTimeout(timer);
    } else if (timeLeft === 0 && gameState === 'playing') {
      handleTimeout();
    }
  }, [timeLeft, gameState]);

  const loadLevels = async () => {
    try {
      const response = await fetch(`${API}/games/${game.id}/levels`);
      const data = await response.json();
      setLevels(data.levels || []);
      setLoading(false);
    } catch (error) {
      console.error('Error loading levels:', error);
      setLoading(false);
    }
  };

  const startLevel = async (level) => {
    try {
      setLoading(true);
      
      // Add script parameter to API call
      const scriptParam = selectedScript ? `?script=${selectedScript}` : '';
      const response = await fetch(`${API}/games/${game.id}/questions/${level.level}${scriptParam}`, {
        headers: { 'Authorization': `Bearer ${userToken}` }
      });
      
      if (!response.ok) {
        throw new Error(`Failed to load questions: ${response.status}`);
      }
      
      const data = await response.json();
      
      // Check if we have questions
      if (!data.questions || data.questions.length === 0) {
        showNotification(`No questions available for ${selectedScript} script at this level`, 'error');
        setLoading(false);
        return;
      }
      
      setQuestions(data.questions);
      setSelectedLevel(data.level_config);
      setMaxAttempts(data.level_config.attempts_per_question);
      setTimeLeft(data.level_config.time_per_question);
      setHintsAvailable(data.level_config.hints_available || 0);
      setAttempts(0);
      setScore(0);
      setCorrectAnswers(0);
      setHintsUsed(0);
      setCurrentQuestionIndex(0);
      setGameState('playing');
      setLoading(false);
    } catch (error) {
      console.error('Error starting level:', error);
      showNotification('Failed to load questions. Please try again.', 'error');
      setLoading(false);
    }
  };

  const handleSubmitAnswer = async () => {
    if (!userAnswer.trim()) return;

    try {
      const currentQuestion = questions[currentQuestionIndex];
      const response = await fetch(`${API}/games/${game.id}/verify-answer`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${userToken}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          question_id: currentQuestion.id,
          answer: userAnswer
        })
      });

      const data = await response.json();
      
      if (data.correct) {
        const pointsEarned = selectedLevel.points_per_correct - (hintsUsed > 0 ? 5 : 0);
        setScore(score + pointsEarned);
        setCorrectAnswers(correctAnswers + 1);
        setFeedback({ type: 'success', message: `🎉 Correct! +${pointsEarned} points` });
        setShowCelebration(true);
        
        setTimeout(() => {
          setShowCelebration(false);
          nextQuestion();
        }, 1500);
      } else {
        const currentAttempt = attempts;
        setAttempts(attempts + 1);
        
        // Trigger heart loss animation
        setHeartLossIndex(currentAttempt);
        setTimeout(() => setHeartLossIndex(-1), 600);
        
        if (attempts + 1 >= maxAttempts) {
          setFeedback({ type: 'error', message: `Out of attempts! Answer: ${data.correct_answer}` });
          setTimeout(() => {
            nextQuestion();
          }, 2000);
        } else {
          setFeedback({ type: 'error', message: `Wrong! ${maxAttempts - attempts - 1} attempts left` });
        }
      }
    } catch (error) {
      console.error('Error verifying answer:', error);
      showNotification('Error checking answer', 'error');
    }
  };

  const handleGetHint = async () => {
    if (hintsUsed >= hintsAvailable) return;

    try {
      const currentQuestion = questions[currentQuestionIndex];
      const response = await fetch(`${API}/games/${game.id}/hint`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${userToken}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ question_id: currentQuestion.id })
      });

      const data = await response.json();
      setHintText(data.hint);
      setShowHint(true);
      setHintsUsed(hintsUsed + 1);
    } catch (error) {
      console.error('Error getting hint:', error);
    }
  };

  const nextQuestion = () => {
    if (currentQuestionIndex + 1 < questions.length) {
      setCurrentQuestionIndex(currentQuestionIndex + 1);
      setUserAnswer('');
      setAttempts(0);
      setFeedback(null);
      setShowHint(false);
      setHeartLossIndex(-1);
      setTimeLeft(selectedLevel.time_per_question);
    } else {
      endGame();
    }
  };

  const handleTimeout = () => {
    setShowTimeUpModal(true);
  };

  const handleTimeUpContinue = () => {
    setShowTimeUpModal(false);
    nextQuestion();
  };

  const endGame = async () => {
    try {
      const accuracy = (correctAnswers / questions.length) * 100;
      await fetch(`${API}/games/${game.id}/score`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${userToken}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          level: selectedLevel.level,
          score: score,
          questions_answered: questions.length,
          correct_answers: correctAnswers,
          accuracy: accuracy,
          time_taken: (selectedLevel.time_per_question * questions.length) - timeLeft,
          hints_used: hintsUsed,
          completed: true
        })
      });

      setGameState('results');
    } catch (error) {
      console.error('Error submitting score:', error);
      setGameState('results');
    }
  };

  // Level Select Screen
  if (gameState === 'level-select') {
    return (
      <div className="space-y-6 max-w-6xl mx-auto">
        <div className="flex items-center gap-4 mb-6">
          <button
            onClick={onBack}
            className={`px-4 py-2 rounded-lg font-semibold transition-all shadow-md hover:shadow-lg ${
              isDarkMode ? 'bg-gray-700 hover:bg-gray-600 text-gray-300' : 'bg-gray-200 hover:bg-gray-300 text-gray-700'
            }`}
          >
            ← Back
          </button>
          <div>
            <h2 className={`text-3xl font-bold ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>
              {t('games_select_level')}
            </h2>
            <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
              {selectedScript === 'arabic' ? t('games_arabic_script') : t('games_abc_latin_script')}
            </p>
          </div>
        </div>

        {loading ? (
          <div className="text-center py-12">
            <div className="animate-spin inline-block w-12 h-12 border-4 border-blue-500 border-t-transparent rounded-full"></div>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {levels.map((level) => {
              const levelData = [
                { gradient: 'from-green-500 to-emerald-600', shadow: 'hover:shadow-green-500/50' },
                { gradient: 'from-yellow-500 to-orange-500', shadow: 'hover:shadow-yellow-500/50' },
                { gradient: 'from-orange-500 to-red-500', shadow: 'hover:shadow-orange-500/50' },
                { gradient: 'from-red-500 to-pink-600', shadow: 'hover:shadow-red-500/50' }
              ];
              const style = levelData[level.level - 1] || levelData[0];
              
              return (
                <div
                  key={level.id}
                  onClick={() => startLevel(level)}
                  className={`group relative p-8 rounded-2xl cursor-pointer transition-all duration-300 transform hover:-translate-y-2 ${
                    isDarkMode
                      ? 'bg-gray-800 shadow-xl hover:shadow-2xl'
                      : 'bg-white shadow-lg hover:shadow-2xl'
                  } ${style.shadow} border ${isDarkMode ? 'border-gray-700' : 'border-gray-200'}`}
                >
                  {/* Gradient Accent Bar */}
                  <div className={`absolute top-0 left-0 right-0 h-2 bg-gradient-to-r ${style.gradient} rounded-t-2xl`}></div>
                  
                  <div className="flex justify-between items-start mb-6 mt-2">
                    <div>
                      <h3 className={`text-3xl font-bold mb-1 ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>
                        Level {level.level}
                      </h3>
                      <p className={`text-xl font-semibold bg-gradient-to-r ${style.gradient} bg-clip-text text-transparent`}>
                        {level.name}
                      </p>
                    </div>
                    <div className={`text-5xl transform group-hover:scale-110 transition-transform duration-300`}>
                      {level.level === 1 ? '🟢' : level.level === 2 ? '🟡' : level.level === 3 ? '🟠' : '🔴'}
                    </div>
                  </div>
                  
                  <div className={`grid grid-cols-2 gap-3 mb-6 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                    <div className={`p-3 rounded-lg ${isDarkMode ? 'bg-gray-700' : 'bg-gray-50'}`}>
                      <p className="text-2xl mb-1">⏱️</p>
                      <p className="text-sm font-semibold">{level.time_per_question}s</p>
                      <p className="text-xs opacity-70">per question</p>
                    </div>
                    <div className={`p-3 rounded-lg ${isDarkMode ? 'bg-gray-700' : 'bg-gray-50'}`}>
                      <p className="text-2xl mb-1">❤️</p>
                      <p className="text-sm font-semibold">{level.attempts_per_question}</p>
                      <p className="text-xs opacity-70">attempts</p>
                    </div>
                    <div className={`p-3 rounded-lg ${isDarkMode ? 'bg-gray-700' : 'bg-gray-50'}`}>
                      <p className="text-2xl mb-1">💡</p>
                      <p className="text-sm font-semibold">{level.hints_available || 0}</p>
                      <p className="text-xs opacity-70">hints</p>
                    </div>
                    <div className={`p-3 rounded-lg ${isDarkMode ? 'bg-gray-700' : 'bg-gray-50'}`}>
                      <p className="text-2xl mb-1">🎯</p>
                      <p className="text-sm font-semibold">{level.pass_score}%</p>
                      <p className="text-xs opacity-70">{t('games_to_pass')}</p>
                    </div>
                  </div>

                  <div className={`p-4 rounded-lg mb-4 ${isDarkMode ? 'bg-gray-700' : 'bg-gray-50'}`}>
                    <p className="text-sm opacity-70 mb-1">{t('games_reward')}</p>
                    <p className="text-lg font-bold">⭐ {level.points_per_correct} points per correct answer</p>
                  </div>

                  <button className={`w-full py-4 rounded-lg font-bold text-lg bg-gradient-to-r ${style.gradient} text-white shadow-lg hover:shadow-xl transition-all transform group-hover:scale-105`}>
                    {t('games_start_level')}
                  </button>
                </div>
              );
            })}
          </div>
        )}
      </div>
    );
  }

  // Playing Screen
  if (gameState === 'playing') {
    // Auto-resume from URL is in flight (selectedLevel/questions not loaded yet).
    // Show a loading spinner instead of crashing on selectedLevel.level access.
    if (!selectedLevel || questions.length === 0) {
      return (
        <div className="min-h-[300px] flex items-center justify-center">
          <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-600"></div>
        </div>
      );
    }
    const currentQuestion = questions[currentQuestionIndex];
    const progress = ((currentQuestionIndex + 1) / questions.length) * 100;

    return (
      <div className="space-y-6 max-w-4xl mx-auto">
        {/* Hidden Audio Element */}
        {game.audio_url && (
          <audio 
            ref={audioRef} 
            src={game.audio_url} 
            loop 
            preload="auto"
            style={{ display: 'none' }}
          />
        )}

        {/* Top Controls */}
        <div className="flex items-center gap-3">
          {/* Back Button */}
          <button
            onClick={() => setShowExitModal(true)}
            className={`px-4 py-2 rounded-lg font-semibold transition-all ${
              isDarkMode ? 'bg-gray-700 hover:bg-gray-600 text-gray-300' : 'bg-gray-200 hover:bg-gray-300 text-gray-700'
            }`}
          >
            ← Back
          </button>

          {/* Music Control Button - Only shows if audio URL exists */}
          {game.audio_url && (
            <button
              onClick={() => {
                if (audioRef.current) {
                  if (audioRef.current.paused) {
                    audioRef.current.play();
                    setIsMuted(false);
                  } else {
                    audioRef.current.pause();
                    setIsMuted(true);
                  }
                }
              }}
              className={`px-4 py-2 rounded-lg font-semibold transition-all shadow-md ${
                isMuted
                  ? isDarkMode ? 'bg-gray-600 hover:bg-gray-700 text-white' : 'bg-gray-400 hover:bg-gray-500 text-white'
                  : isDarkMode ? 'bg-green-600 hover:bg-green-700 text-white' : 'bg-green-500 hover:bg-green-600 text-white'
              }`}
              title={isMuted ? 'Play Music' : 'Pause Music'}
            >
              {isMuted ? '▶️ Play' : '⏸️ Pause'}
            </button>
          )}
        </div>

        {/* Header */}
        <div className={`p-6 rounded-xl ${isDarkMode ? 'bg-gray-800' : 'bg-white'} shadow-lg`}>
          <div className="flex justify-between items-center mb-4">
            <div className="flex items-center gap-4">
              <span className={`text-lg font-semibold ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>
                Level {selectedLevel.level} - {selectedLevel.name}
              </span>
              <span className={isDarkMode ? 'text-gray-400' : 'text-gray-600'}>
                Question {currentQuestionIndex + 1}/{questions.length}
              </span>
            </div>
            <div className="flex items-center gap-4">
              <div className={`text-2xl font-bold ${timeLeft < 10 ? 'text-red-500 animate-pulse' : isDarkMode ? 'text-white' : 'text-gray-800'}`}>
                ⏱️ {timeLeft}s
              </div>
              <div className={`text-xl ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>
                🎯 {score}
              </div>
            </div>
          </div>

          {/* Progress Bar */}
          <div className={`h-2 rounded-full ${isDarkMode ? 'bg-gray-700' : 'bg-gray-200'}`}>
            <div 
              className="h-full bg-gradient-to-r from-blue-600 to-purple-600 rounded-full transition-all duration-300"
              style={{ width: `${progress}%` }}
            />
          </div>

          {/* Attempts */}
          <div className="flex gap-1 mt-4">
            <style>{`
              @keyframes heartLoss {
                0% { transform: scale(1); filter: brightness(1); }
                25% { transform: scale(3.5); filter: brightness(1.5); }
                50% { transform: scale(3.5) rotate(10deg); filter: brightness(2); }
                75% { transform: scale(3.5) rotate(-10deg); filter: brightness(1.5); }
                100% { transform: scale(1); filter: brightness(1); }
              }
            `}</style>
            {[...Array(maxAttempts)].map((_, i) => (
              <div
                key={i}
                className={`text-2xl transition-all duration-300 ${
                  i < attempts 
                    ? 'opacity-30' 
                    : ''
                } ${
                  i === heartLossIndex 
                    ? 'text-red-600' 
                    : ''
                }`}
                style={i === heartLossIndex ? {
                  animation: 'heartLoss 0.6s ease-in-out'
                } : {}}
              >
                ❤️
              </div>
            ))}
          </div>
        </div>

        {/* Question */}
        <div className={`p-8 rounded-xl ${isDarkMode ? 'bg-gray-800' : 'bg-white'} shadow-lg`}>
          <div className="mb-6">
            <p className={`text-sm mb-2 ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
              English Meaning:
            </p>
            <div className={`p-4 rounded-lg ${isDarkMode ? 'bg-gray-700' : 'bg-blue-50'}`}>
              <p className={`text-xl font-semibold ${isDarkMode ? 'text-blue-300' : 'text-blue-700'}`}>
                "{currentQuestion.english_hint}"
              </p>
            </div>
          </div>

          <div className="mb-6">
            <p className={`text-sm mb-2 ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
              Complete the Darija sentence:
            </p>
            <div className={`p-4 rounded-lg ${isDarkMode ? 'bg-gray-700' : 'bg-gray-50'}`}>
              <p className={`text-2xl font-bold ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>
                {currentQuestion.display_sentence}
              </p>
            </div>
          </div>

          <div className="mb-4">
            <p className={`text-sm mb-2 ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
              Your Answer:
            </p>
            <div className="flex gap-2">
              <input
                type="text"
                value={userAnswer}
                onChange={(e) => setUserAnswer(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && handleSubmitAnswer()}
                className={`flex-1 px-4 py-3 rounded-lg text-lg ${
                  isDarkMode
                    ? 'bg-gray-700 text-white border-gray-600'
                    : 'bg-white text-gray-800 border-gray-300'
                } border-2 focus:outline-none focus:border-blue-500`}
                placeholder="Type the missing word..."
                autoFocus
              />
              {hintsAvailable > 0 && hintsUsed < hintsAvailable && (
                <button
                  onClick={handleGetHint}
                  className="px-4 py-3 bg-yellow-600 hover:bg-yellow-700 text-white rounded-lg font-semibold transition-colors"
                >
                  💡 Hint ({hintsAvailable - hintsUsed})
                </button>
              )}
            </div>

            {showHint && (
              <div className="mt-2 p-3 bg-yellow-100 text-yellow-900 rounded-lg">
                💡 Hint: {hintText}
              </div>
            )}
          </div>

          <button
            onClick={handleSubmitAnswer}
            disabled={!userAnswer.trim()}
            className={`w-full py-4 rounded-lg font-bold text-lg transition-all ${
              userAnswer.trim()
                ? 'bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white'
                : 'bg-gray-400 cursor-not-allowed text-gray-200'
            }`}
          >
            ✓ Submit Answer
          </button>

          {feedback && (
            <div className={`mt-4 p-4 rounded-lg text-center font-semibold ${
              feedback.type === 'success'
                ? 'bg-green-100 text-green-800'
                : 'bg-red-100 text-red-800'
            }`}>
              {feedback.message}
            </div>
          )}
        </div>

        {/* Celebration Animation */}
        {showCelebration && (
          <div className="fixed inset-0 flex items-center justify-center pointer-events-none z-50">
            <div className="animate-bounce text-8xl">
              🎉
            </div>
          </div>
        )}

        {/* Time Warning */}
        {showTimeWarning && (
          <div className="fixed top-20 left-1/2 transform -translate-x-1/2 z-50 animate-pulse">
            <div className="bg-yellow-500 text-white px-6 py-3 rounded-lg shadow-2xl font-bold text-lg">
              ⚠️ Hurry! Time is running out!
            </div>
          </div>
        )}

        {/* Time Up Modal */}
        {showTimeUpModal && (
          <div className="fixed inset-0 bg-black bg-opacity-70 flex items-center justify-center z-50 p-4">
            <div className={`rounded-xl p-8 max-w-md w-full text-center ${isDarkMode ? 'bg-gray-800' : 'bg-white'} shadow-2xl`}>
              <div className="text-6xl mb-4">⏰</div>
              <h3 className={`text-2xl font-bold mb-4 ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>
                Time's Up!
              </h3>
              <p className={`text-lg mb-6 ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                Don't worry! Keep going - you're doing great! 💪
              </p>
              <button
                onClick={handleTimeUpContinue}
                className="w-full py-3 bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white rounded-lg font-bold text-lg"
              >
                Continue to Next Question →
              </button>
            </div>
          </div>
        )}

        {/* Exit Confirmation Modal */}
        {showExitModal && (
          <div className="fixed inset-0 bg-black bg-opacity-80 backdrop-blur-sm flex items-center justify-center z-50 p-4 animate-fadeIn">
            <div className={`rounded-2xl p-8 max-w-md w-full text-center transform transition-all ${
              isDarkMode ? 'bg-gradient-to-br from-gray-800 to-gray-900' : 'bg-gradient-to-br from-white to-gray-50'
            } shadow-2xl border-2 ${isDarkMode ? 'border-orange-500' : 'border-orange-400'}`}>
              {/* Warning Icon */}
              <div className="text-7xl mb-4 animate-bounce">⚠️</div>
              
              {/* Title */}
              <h3 className={`text-3xl font-bold mb-3 ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>
                Leave Game?
              </h3>
              
              {/* Message */}
              <p className={`text-lg mb-2 ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                Are you sure you want to exit?
              </p>
              <p className={`text-base mb-6 font-semibold ${isDarkMode ? 'text-orange-400' : 'text-orange-600'}`}>
                Your progress will be lost! 😢
              </p>

              {/* Current Progress Info */}
              <div className={`p-4 rounded-lg mb-6 ${isDarkMode ? 'bg-gray-700' : 'bg-gray-100'}`}>
                <p className={`text-sm mb-2 ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                  {t('games_current_progress')}
                </p>
                <div className="flex justify-around text-center">
                  <div>
                    <p className={`text-2xl font-bold ${isDarkMode ? 'text-blue-400' : 'text-blue-600'}`}>
                      {currentQuestionIndex + 1}/{questions.length}
                    </p>
                    <p className={`text-xs ${isDarkMode ? 'text-gray-500' : 'text-gray-500'}`}>{t('games_questions')}</p>
                  </div>
                  <div>
                    <p className={`text-2xl font-bold ${isDarkMode ? 'text-green-400' : 'text-green-600'}`}>
                      {score}
                    </p>
                    <p className={`text-xs ${isDarkMode ? 'text-gray-500' : 'text-gray-500'}`}>{t('games_score')}</p>
                  </div>
                  <div>
                    <p className={`text-2xl font-bold ${isDarkMode ? 'text-yellow-400' : 'text-yellow-600'}`}>
                      {correctAnswers}
                    </p>
                    <p className={`text-xs ${isDarkMode ? 'text-gray-500' : 'text-gray-500'}`}>{t('games_correct')}</p>
                  </div>
                </div>
              </div>

              {/* Action Buttons */}
              <div className="flex gap-3">
                {/* Stay Button */}
                <button
                  onClick={() => setShowExitModal(false)}
                  className="flex-1 py-4 bg-gradient-to-r from-green-500 to-emerald-600 hover:from-green-600 hover:to-emerald-700 text-white rounded-xl font-bold text-lg shadow-lg hover:shadow-xl transition-all transform hover:scale-105"
                >
                  ✅ Stay & Continue
                </button>
                
                {/* Leave Button */}
                <button
                  onClick={() => {
                    setShowExitModal(false);
                    setGameState('level-select');
                    setScore(0);
                    setCurrentQuestionIndex(0);
                    setCorrectAnswers(0);
                    setAttempts(0);
                    setTimeLeft(selectedLevel.time_per_question);
                  }}
                  className={`flex-1 py-4 rounded-xl font-bold text-lg shadow-lg hover:shadow-xl transition-all ${
                    isDarkMode 
                      ? 'bg-gray-700 hover:bg-gray-600 text-gray-300' 
                      : 'bg-gray-300 hover:bg-gray-400 text-gray-700'
                  }`}
                >
                  ❌ Leave Game
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  }

  // Results Screen
  if (gameState === 'results') {
    const accuracy = (correctAnswers / questions.length) * 100;
    const passed = accuracy >= selectedLevel.pass_score;

    return (
      <div className="space-y-6 max-w-2xl mx-auto">
        {/* Animated Confetti Background */}
        {passed && (
          <div className="fixed inset-0 pointer-events-none z-40">
            <div className="absolute top-0 left-1/4 text-4xl animate-bounce">🎊</div>
            <div className="absolute top-10 right-1/4 text-4xl animate-bounce" style={{animationDelay: '0.2s'}}>✨</div>
            <div className="absolute top-20 left-1/3 text-4xl animate-bounce" style={{animationDelay: '0.4s'}}>🌟</div>
            <div className="absolute top-5 right-1/3 text-4xl animate-bounce" style={{animationDelay: '0.6s'}}>🎉</div>
          </div>
        )}
        
        <div className={`p-8 rounded-xl ${isDarkMode ? 'bg-gray-800' : 'bg-white'} shadow-2xl text-center relative z-50`}>
          <div className="text-8xl mb-6 animate-pulse">
            {passed ? '🏆' : '💪'}
          </div>
          
          <h2 className={`text-4xl font-bold mb-3 ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>
            {passed ? '🎉 Amazing! Level Complete! 🎉' : 'Good Effort! Keep Trying!'}
          </h2>
          
          {passed && (
            <p className={`text-2xl mb-4 font-semibold ${isDarkMode ? 'text-green-400' : 'text-green-600'}`}>
              You've mastered Level {selectedLevel.level}!
            </p>
          )}
          
          <p className={`text-xl mb-6 ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
            {passed 
              ? "Outstanding performance! You're a Darija champion! 🌟" 
              : "Practice makes perfect! Try again and you'll ace it! 🚀"}
          </p>

          <div className={`grid grid-cols-2 gap-4 mb-6 p-6 rounded-lg ${isDarkMode ? 'bg-gray-700' : 'bg-gray-50'}`}>
            <div>
              <div className="text-3xl font-bold text-blue-600">{score}</div>
              <div className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>{t('games_total_score')}</div>
            </div>
            <div>
              <div className="text-3xl font-bold text-green-600">{accuracy.toFixed(0)}%</div>
              <div className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>{t('games_accuracy')}</div>
            </div>
            <div>
              <div className="text-3xl font-bold text-purple-600">{correctAnswers}/{questions.length}</div>
              <div className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>{t('games_correct')}</div>
            </div>
            <div>
              <div className="text-3xl font-bold text-yellow-600">{hintsUsed}</div>
              <div className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>{t('games_hints_used')}</div>
            </div>
          </div>

          <div className="flex gap-4">
            <button
              onClick={() => startLevel(selectedLevel)}
              className="flex-1 py-3 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-bold transition-colors"
            >
              🔄 Play Again
            </button>
            <button
              onClick={() => setGameState('level-select')}
              className={`flex-1 py-3 rounded-lg font-bold transition-colors ${
                isDarkMode
                  ? 'bg-gray-700 hover:bg-gray-600 text-white'
                  : 'bg-gray-200 hover:bg-gray-300 text-gray-800'
              }`}
            >
              📊 Change Level
            </button>
            <button
              onClick={onBack}
              className={`flex-1 py-3 rounded-lg font-bold transition-colors ${
                isDarkMode
                  ? 'bg-gray-700 hover:bg-gray-600 text-white'
                  : 'bg-gray-200 hover:bg-gray-300 text-gray-800'
              }`}
            >
              🏠 Home
            </button>
          </div>
        </div>
      </div>
    );
  }

  return null;
};


export default GamesSection;
